import { useState } from 'react';
import {
  Badge,
  Card,
  CardDescription,
  CardTitle,
  ConfidenceBadge,
  EmptyState,
  ErrorState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  Tooltip,
  buttonVariants,
  cn,
} from '@mgdlead/ui';
import { Download, FileSpreadsheet, FileText, Lock, Printer } from 'lucide-react';
import { can } from '@mgdlead/shared';

import { Link } from 'react-router';

import { PageHeader } from '@mgdlead/ui';
import { FormAlert } from '@mgdlead/ui';
import { NativeSelect } from '@mgdlead/ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { workspacePath } from '../../../features/workspace/navigation';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import {
  reportCsvUrl,
  reportPdfUrl,
  useReportCampaigns,
  useReportPreview,
  type ReportDocument,
  type ReportRow,
} from '../../../lib/api';
import {
  CONFIANCA,
  SECAO_RESUMIDA,
  TRACO,
  diaLongo,
  valorNaTela,
} from '../../../features/report/report-ui';
import { PeriodFilter } from './AnalyticsShell';
import { type PeriodoId } from './analytics-ui';

/**
 * Relatórios — escolher o recorte, **ver o que vai sair** e baixar.
 *
 * ### Por que existe uma prévia
 *
 * Porque baixar às cegas é a forma mais fácil de mandar para outra pessoa um arquivo que
 * ninguém abriu. Um relatório de 90 dias que volta vazio é uma viagem perdida — e pior,
 * ninguém descobre que estava vazio antes de a pergunta já ter sido respondida errado.
 *
 * A prévia **não é um resumo aproximado**: ela é o mesmo objeto que o servidor vai virar
 * CSV e PDF (`report.preview` devolve o retorno de `buildWorkspaceReport`). Se estivesse
 * calculada separado, a tela prometeria uma coisa e o arquivo entregaria outra.
 *
 * ### Nada aqui calcula (invariante 3)
 *
 * Nenhuma soma, nenhuma divisão, nenhuma regra de "confiança baixa". Valor, unidade, grau de
 * confiança e a frase do vazio chegam prontos. O que a tela faz é escolher tipografia.
 */
export function RelatoriosPage() {
  const { workspace, role } = useWorkspace();
  const [periodo, setPeriodo] = useState<PeriodoId>('D30');
  const [campanha, setCampanha] = useState<string>('todas');
  useDocumentTitle(`Relatórios · ${workspace.name}`);

  const podeExportar = can(role, 'analytics:export');
  const campaignId = campanha === 'todas' ? null : campanha;

  const campanhas = useReportCampaigns(workspace.id, podeExportar);
  const previa = useReportPreview(workspace.id, periodo, campaignId, podeExportar);

  /**
   * Quem não tem `analytics:export` não vê a tela — e nem os números dela.
   *
   * O item de menu já está escondido para esse papel; isto aqui é o caso de quem chegou
   * pela URL. Esconder é UX; quem barra é o servidor (`permissionProcedure`), que recusa a
   * prévia e os dois downloads de qualquer jeito.
   */
  if (!podeExportar) {
    return (
      <>
        <PageHeader title="Relatórios" />
        {/*
          `kind="no-data"` porque o `EmptyState` só distingue "sem medição" de "zero
          medido" — falta de permissão não é nenhum dos dois, e o título e a descrição
          explícitos são quem conta a história aqui.
        */}
        <EmptyState
          kind="no-data"
          icon={Lock}
          title="Você não tem permissão para exportar"
          description="Exportar os números do painel é permissão de Analista para cima. Peça a quem administra o workspace."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Relatórios"
        description="Escolha o período e a campanha, confira os números e baixe em CSV ou PDF."
      />

      {can(role, 'group:export_contacts') ? (
        <p className="mb-3 text-xs text-muted-foreground">
          Procurando a lista de contatos dos grupos? Fica em{' '}
          <Link
            className="text-primary underline-offset-2 hover:underline"
            to={workspacePath(workspace.slug, 'contatos')}
          >
            Contatos
          </Link>
          .
        </p>
      ) : null}

      <PeriodFilter value={periodo} onChange={setPeriodo} timezone={workspace.timezone} />

      <Card className="mb-3">
        <label htmlFor="relatorio-campanha" className="block text-xs font-medium">
          Campanha
        </label>
        <NativeSelect
          id="relatorio-campanha"
          className="mt-1 w-full"
          value={campanha}
          onChange={(e) => setCampanha(e.target.value)}
        >
          <option value="todas">Todas as campanhas</option>
          {(campanhas.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.code})
            </option>
          ))}
        </NativeSelect>
        <p className="mt-1.5 text-2xs text-muted-foreground">
          Com uma campanha escolhida, o relatório traz a <strong>atribuição dela</strong> e os
          grupos vinculados. Entrada e saída são fatos do grupo — e o mesmo grupo pode servir várias
          campanhas, então não existe “total de entradas da campanha”.
        </p>
      </Card>

      {previa.isError ? (
        <ErrorState
          title="Não foi possível montar a prévia"
          description={previa.error.message}
          onRetry={() => void previa.refetch()}
        />
      ) : (
        <>
          <BaixarCard
            workspaceId={workspace.id}
            periodo={periodo}
            campaignId={campaignId}
            filename={previa.data?.filename}
            vazio={previa.data?.report.empty}
            carregando={previa.isPending}
          />
          <PreviaCard report={previa.data?.report} carregando={previa.isPending} />
        </>
      )}
    </>
  );
}

/* ========================================================================== */
/*  Baixar                                                                     */
/* ========================================================================== */

function BaixarCard({
  workspaceId,
  periodo,
  campaignId,
  filename,
  vazio,
  carregando,
}: {
  workspaceId: string;
  periodo: PeriodoId;
  campaignId: string | null;
  filename: string | undefined;
  vazio: boolean | undefined;
  carregando: boolean;
}) {
  return (
    <Card className="mb-3">
      <CardTitle>Baixar</CardTitle>
      <CardDescription>
        Os dois arquivos saem do mesmo recorte que está na prévia abaixo.
      </CardDescription>

      {vazio === true ? (
        <FormAlert tone="warning" className="mt-3">
          O período escolhido <strong>não tem nenhum dado medido</strong>. O arquivo sai dizendo
          isso, com as células vazias — e não com zeros, que fariam parecer que ninguém entrou
          quando o que houve foi não estarmos olhando. Se isso surpreende, experimente um período
          maior.
        </FormAlert>
      ) : null}

      {/*
        Links, e não botões com `fetch`: o navegador salva o CSV com o nome que o servidor
        mandou (`Content-Disposition`) e abre o documento de impressão numa aba própria. No
        celular a ação importante ocupa a largura toda (DESIGN_SYSTEM §11).
      */}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <a
          href={reportCsvUrl(workspaceId, periodo, campaignId)}
          download
          className={cn(buttonVariants({ variant: 'primary' }), 'w-full gap-1.5 sm:w-auto')}
        >
          <Download aria-hidden="true" className="size-4" />
          Baixar CSV
        </a>
        <a
          href={reportPdfUrl(workspaceId, periodo, campaignId)}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(buttonVariants({ variant: 'secondary' }), 'w-full gap-1.5 sm:w-auto')}
        >
          <Printer aria-hidden="true" className="size-4" />
          Baixar PDF
        </a>
      </div>

      <ul className="mt-3 flex flex-col gap-1 text-2xs text-muted-foreground">
        <li className="flex gap-1.5">
          <FileSpreadsheet aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
          <span>
            <strong>CSV</strong> abre no Excel em português (separador “;”, acento certo). Cada
            número leva o <strong>grau de confiança da atribuição em coluna própria</strong>, e
            célula vazia quer dizer “não há o que medir”, nunca zero.
            {carregando || filename === undefined ? null : (
              <>
                {' '}
                Nome do arquivo: <code className="nums">{filename}</code>.
              </>
            )}
          </span>
        </li>
        <li className="flex gap-1.5">
          <FileText aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
          <span>
            <strong>PDF</strong> abre numa aba com a caixa de impressão pronta — escolha “Salvar
            como PDF”. As cores do documento saem impressas mesmo sem marcar “gráficos de plano de
            fundo”.
          </span>
        </li>
      </ul>
    </Card>
  );
}

/* ========================================================================== */
/*  Prévia                                                                     */
/* ========================================================================== */

function PreviaCard({
  report,
  carregando,
}: {
  report: ReportDocument | undefined;
  carregando: boolean;
}) {
  if (carregando || report === undefined) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>O que vai sair no arquivo</CardTitle>
        <div className="mt-3 h-64 animate-pulse rounded-md bg-muted" />
      </Card>
    );
  }

  /* As seções saem na ordem em que o servidor as devolveu. A tela não reordena. */
  const secoes: Array<{ nome: string; linhas: ReportRow[] }> = [];
  for (const row of report.rows) {
    const atual = secoes.at(-1);
    if (atual !== undefined && atual.nome === row.section) atual.linhas.push(row);
    else secoes.push({ nome: row.section, linhas: [row] });
  }

  return (
    <Card padded={false} className="p-3 md:p-4">
      <CardTitle>O que vai sair no arquivo</CardTitle>
      <CardDescription>
        {report.workspaceName} · {diaLongo(report.period.from)} a {diaLongo(report.period.to)} ·{' '}
        {report.campaign === null
          ? 'todas as campanhas'
          : `${report.campaign.name} (${report.campaign.code})`}
      </CardDescription>
      <p className="mt-1 text-2xs text-muted-foreground">
        Cada número leva o <strong>grau de confiança da atribuição</strong> — é a mesma coluna que
        vai no arquivo. Travessão quer dizer “não há o que medir”, nunca zero.
      </p>

      {report.empty ? (
        <FormAlert tone="info" className="mt-3">
          {report.emptyReason}
        </FormAlert>
      ) : null}

      {report.notes.map((nota) => (
        <p key={nota} className="mt-3 border-l-2 border-border pl-2 text-2xs text-muted-foreground">
          {nota}
        </p>
      ))}

      {secoes.length === 0 ? (
        <EmptyState
          kind="no-data"
          icon={FileText}
          title="Nada a exportar neste recorte"
          description="Nenhum grupo, campanha ou evento entra neste período."
          compact
        />
      ) : (
        secoes.map((secao) => (
          <SecaoPrevia key={secao.nome} nome={secao.nome} linhas={secao.linhas} />
        ))
      )}
    </Card>
  );
}

/**
 * Quantas linhas de cada seção aparecem na prévia.
 *
 * A série diária de 90 dias são 360 linhas: mostrar todas transformaria a prévia num
 * relatório, e no celular ninguém rolaria até o botão. O que a prévia precisa responder é
 * "o arquivo vem cheio ou vem vazio?" — e o rodapé diz quantas linhas ficaram de fora, para
 * o corte nunca parecer o fim do dado.
 */
const LINHAS_NA_PREVIA = 8;

/**
 * Uma seção da prévia — **três colunas, e nunca quatro.**
 *
 * O item (o dia, a campanha, o grupo) entra como segunda linha DENTRO da célula da métrica,
 * em vez de virar coluna própria. Não é economia de espaço: com quatro colunas em 390px a
 * tabela ganha rolagem horizontal e a coluna de confiança sai da tela — justamente a coluna
 * que é o critério de aceite desta fase. Uma tela que esconde o que a fase entrega é a
 * mesma classe de erro de "funcionalidade sem caminho": existe no código e não existe para
 * quem usa. Conferido por screenshot em 390×844, que é onde o dono olha.
 */
function SecaoPrevia({ nome, linhas }: { nome: string; linhas: ReportRow[] }) {
  const visiveis = linhas.slice(0, LINHAS_NA_PREVIA);
  const escondidas = linhas.length - visiveis.length;

  return (
    <section className="mt-4">
      <h3 className="text-sm font-medium">{nome}</h3>
      {SECAO_RESUMIDA[nome] ? (
        <p className="text-2xs text-muted-foreground">{SECAO_RESUMIDA[nome]}</p>
      ) : null}

      <Table caption={nome} density="dense" className="mt-2" zebra>
        <TableHeader>
          <TableRow>
            <TableHead>Métrica</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead>Confiança</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visiveis.map((linha, i) => (
            <TableRow key={`${linha.item}-${linha.metric}-${i}`}>
              <TableRowHeader>
                <span className="block">{linha.metric}</span>
                {linha.item === '' ? null : (
                  <span className="block truncate text-2xs font-normal text-muted-foreground">
                    {linha.item}
                  </span>
                )}
              </TableRowHeader>
              <TableCell nowrap>
                <Valor linha={linha} />
              </TableCell>
              <TableCell nowrap>
                <Confianca linha={linha} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {escondidas > 0 ? (
        <p className="mt-1.5 text-2xs text-muted-foreground">
          + {escondidas} {escondidas === 1 ? 'linha' : 'linhas'} desta seção no arquivo completo.
        </p>
      ) : null}
    </section>
  );
}

/**
 * O número, ou o travessão **com a frase**.
 *
 * `0` e vazio são coisas diferentes (invariante 8), e a diferença tem de estar visível sem
 * hover: por isso o travessão é `muted`, sublinhado pontilhado e com a explicação no
 * tooltip — o mesmo tratamento das outras telas do painel.
 */
function Valor({ linha }: { linha: ReportRow }) {
  if (linha.value === null) {
    return (
      <Tooltip content={linha.note || 'Não há o que medir neste recorte.'}>
        <span tabIndex={0} className="cursor-help text-muted-foreground">
          {TRACO}
        </span>
      </Tooltip>
    );
  }
  return <span className="nums font-medium">{valorNaTela(linha.value, linha.unit)}</span>;
}

/**
 * O grau de confiança, colado no número (invariante 9).
 *
 * Quando o número passa por atribuição, quem responde é o `ConfidenceBadge` do design
 * system — o mesmo componente e o mesmo vocabulário do resto do produto. Quando é um fato
 * contado, um selo neutro diz isso com todas as letras: marcar tudo de "provável" ensina
 * quem lê a ignorar a coluna.
 */
function Confianca({ linha }: { linha: ReportRow }) {
  if (linha.value === null || linha.confidence === null) {
    return <span className="text-muted-foreground">{TRACO}</span>;
  }

  const spec = CONFIANCA[linha.confidence];
  if (spec.badge === null) {
    return (
      <Tooltip content={spec.explica}>
        <Badge tone={spec.tone} tabIndex={0} className="cursor-help">
          {spec.label}
        </Badge>
      </Tooltip>
    );
  }
  return <ConfidenceBadge confidence={spec.badge} />;
}
